/**
 * StudiesRealtimeProvider
 *
 * Centralized SSE connection management for study-related real-time updates.
 *
 * This provider ensures that:
 * 1. SSE connection is established once for authenticated users (not per-component)
 * 2. All ["studies", ...] query caches are automatically updated on SSE events
 * 3. Components just use useQuery and receive updates automatically
 *
 * Architecture:
 * - Wrap this around authenticated routes (e.g., in ProtectedLayout)
 * - Individual components do NOT need to call useModernSSE() for cache updates
 * - Components that need live event data (e.g., progress bars) can still use
 *   useModernSSE() directly for the onEvent callback
 */

import { useAuth } from "@/contexts/AuthContext";
import { useModernSSE } from "@/hooks/useModernSSE";
import { isEdge } from "@/utils/browser";
import type { ReactNode } from "react";

interface StudiesRealtimeProviderProps {
  children: ReactNode;
}

export function StudiesRealtimeProvider({ children }: StudiesRealtimeProviderProps) {
  const { aiewToken } = useAuth();

  // Maintain SSE connection and handle cache updates for all study queries.
  // The useModernSSE hook internally:
  // 1. Manages connection via singleton SSEConnectionManager
  // 2. Updates all ["studies", ...] query caches on events
  // 3. Invalidates queries on material status changes (completed/failed/ready)
  useModernSSE({
    enabled: !!aiewToken,
    ...(aiewToken && { token: aiewToken }),
    // Edge browser has SSE issues, use polling fallback
    pollInterval: isEdge() ? 5000 : 0,
  });

  return <>{children}</>;
}
